import { useState } from "react";
import { useSelector } from "react-redux";
import { productsSelector } from "../../../redux/magento/selectors/magento.selectors";
import { eventEditionSelector } from "../../../redux/eventEdition/selectors/eventEdition.selectors";
import PublicApiService from "../../../services/PublicApiService";
import { IPersonBadge, IProduct, Organisation } from "../../../common/types";
import { AvailableDelegateEntitlements, Entitlement, useDelegateUpgrade } from "@reedexpo/mercury-badge-manager-components";
import { useBadgeOrders } from "../../../common/badges/hooks/useBadgeOrders";
import MagentoHelper from "../../../common/network/helpers/magento/MagentoHelper";
import { organisationsSelector } from "../../../redux/organisations/selectors/organisation.selectors";
import { useSnackbar } from "notistack";
import { SNACKBAR_VARIANTS } from "../../../common/constants";
import { useTranslation } from "react-i18next";
import { ExhibitorType } from "../../../common/enums";
import { useUnpaidBadgePayment } from "./useUnpaidBadgePayment/useUnpaidBadgePayment";
import { BadgeOrder } from "../../../redux/types";
import cloneDeep from "lodash.clonedeep";

type Props = {
  badge?: IPersonBadge;
};

export const useUpgradeDialog = ({ badge }: Props) => {
  const [areEntitlementsLoading, setAreEntitlementsLoading] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const products = useSelector(productsSelector);
  const eventEdition = useSelector(eventEditionSelector);
  const { getDelegateBadgeUpgradeProducts, getActivateModalProducts } = useDelegateUpgrade();
  const { getOrdersByBadgeId } = useBadgeOrders();
  const [delegateActivationEntitlements, setDelegateActivationEntitlements] = useState<AvailableDelegateEntitlements>({
    entitlementsBundles: [],
    entitlementsWithProduct: []
  });
  const [upgradeProducts, setUpgradeProducts] = useState<IProduct[]>(products);
  const [upgradeProductsLoading, setUpgradeProductsLoading] = useState(false);
  const organisations = useSelector(organisationsSelector);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation("common");

  const fetchDelegateUpgradeProducts = async (badge: IPersonBadge) => {
    if (!badge.organisation.participatingOrganisationId || badge.customerType !== "exhibitingDelegate") {
      return;
    }
    const organisation = organisations.find(org => org.id === badge.organisation.participatingOrganisationId);
    if (!organisation) {
      setUpgradeProducts([]);
      return;
    }
    setUpgradeProductsLoading(true);
    setAreEntitlementsLoading(true);
    try {
      const entitlementsResponse = await PublicApiService.getCompanyEntitlements(badge.organisation.participatingOrganisationId);
      const entitlements: Entitlement[] = entitlementsResponse.data.entitlements || [];
      const groupedProducts = cloneDeep(MagentoHelper.groupedProducts);
      const bundleProducts = cloneDeep(MagentoHelper.magentoProducts);
      const badgeOrders = getOrdersByBadgeId(badge.id);
      const exhibitorType = getMappedExhibitorType(organisation);

      const delegateEntitlements = getActivateModalProducts(entitlements, groupedProducts, bundleProducts, {
        customerType: badge.customerType,
        productCode: badge.productCode
      });
      const mappedProducts = getDelegateBadgeUpgradeProducts(
        entitlements,
        cloneDeep(badgeOrders),
        groupedProducts,
        bundleProducts,
        badge,
        exhibitorType
      );
      setDelegateActivationEntitlements(delegateEntitlements);
      setUpgradeProducts(
        mappedProducts.filter(product => product.sku === badge.productCode || MagentoHelper.areProductDatesValid(product))
      );
    } catch (e) {
      console.error(e);
      enqueueSnackbar(<b>{t("errors.genericClientError")}</b>, SNACKBAR_VARIANTS.error);
    } finally {
      setUpgradeProductsLoading(false);
      setAreEntitlementsLoading(false);
    }
  };

  const getMappedExhibitorType = (organisation: Organisation) => {
    if (organisation.exhibitorType === "Sharer") {
      return ExhibitorType.SHARER;
    }
    if (organisation.exhibitorType === "MainStandHolder") {
      return ExhibitorType.MSH;
    }
    if (organisation.exhibitorType === "Exhibitor") {
      if (organisation.stands.length > 0) {
        return ExhibitorType.MSH;
      }
      return ExhibitorType.VISITING_COMPANY;
    }
    return ExhibitorType.VISITING_COMPANY;
  };

  const toggleDialog = () => setUpgradeDialogOpen(!upgradeDialogOpen);

  const { getUnpaidBadgeOrder } = useUnpaidBadgePayment({ selectedBadges: [] });

  const getOrdersForBadges = (badges: IPersonBadge[]) => {
    const orders: BadgeOrder[] = [];
    badges.forEach(badge => {
      const order = getUnpaidBadgeOrder(badge);
      orders.push(order[0]!);
    });
    return orders;
  };

  const filteredUpgradeProducts = upgradeProducts.filter(
    product => product.sku === badge?.productCode || MagentoHelper.areProductDatesValid(product)
  );

  return {
    areEntitlementsLoading,
    upgradeDialogOpen,
    upgradeProductsLoading,
    upgradeProducts: filteredUpgradeProducts,
    products,
    eventEdition,
    delegateActivationEntitlements,
    fetchDelegateUpgradeProducts,
    getOrdersForBadges,
    toggleDialog,
    setUpgradeDialogOpen
  };
};
